import { type FC, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const Navbar: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, profile, signOut, isAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: '번호 생성', icon: 'ri-shuffle-line' },
    { path: '/statistics', label: '통계', icon: 'ri-bar-chart-line' },
    { path: '/guide', label: '이용방법', icon: 'ri-book-open-line' },
    ...(user ? [{ path: '/my-numbers', label: '내 번호', icon: 'ri-heart-line' }] : []),
    ...(isAdmin ? [{ path: '/admin', label: '관리자', icon: 'ri-shield-check-line' }] : []),
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200' : 'bg-transparent'}
      `}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-full bg-secondary-500 flex items-center justify-center">
              <i className="ri-flask-line text-white text-sm"></i>
            </div>
            <span className="font-heading text-xl font-bold whitespace-nowrap">
              <span className="text-primary-700">Lotto</span><span className="text-secondary-600">Lab</span>
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => { e.preventDefault(); navigate(link.path); }}
                className={`
                  flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium
                  transition-all duration-200 whitespace-nowrap cursor-pointer
                  ${isActive(link.path)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                  }
                `}
              >
                <i className={`${link.icon} text-base`}></i>
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop user area */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <a
                  href="/account"
                  onClick={(e) => { e.preventDefault(); navigate('/account'); }}
                  className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                >
                  {profile?.nickname || user.email?.split('@')[0]}님
                </a>
                <button
                  onClick={handleLogout}
                  className="
                    px-5 py-2 rounded-full text-sm font-medium
                    border border-background-300 text-foreground-600
                    hover:bg-background-100 transition-all duration-200
                    whitespace-nowrap cursor-pointer
                  "
                >
                  로그아웃
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="
                  px-5 py-2 rounded-full text-sm font-medium
                  border border-background-300 text-foreground-600
                  hover:bg-background-100 transition-all duration-200
                  whitespace-nowrap cursor-pointer
                "
              >
                로그인
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => { e.preventDefault(); navigate(link.path); setMobileOpen(false); }}
                className={`
                  flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium
                  transition-all duration-200 cursor-pointer
                  ${isActive(link.path)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                  }
                `}
              >
                <i className={`${link.icon} text-lg`}></i>
                {link.label}
              </a>
            ))}
            <div className="border-t border-background-200 mt-2 pt-2">
              {user ? (
                <>
                  <div className="px-4 py-2 text-sm text-foreground-500">
                    {profile?.nickname || user.email?.split('@')[0]}님
                  </div>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="
                      w-full mt-1 px-5 py-3 rounded-xl text-sm font-medium
                      border border-background-300 text-foreground-600
                      hover:bg-background-100 transition-all duration-200
                      whitespace-nowrap cursor-pointer
                    "
                  >
                    로그아웃
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                  className="
                    w-full px-5 py-3 rounded-xl text-sm font-medium
                    border border-background-300 text-foreground-600
                    hover:bg-background-100 transition-all duration-200
                    whitespace-nowrap cursor-pointer
                  "
                >
                  로그인
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;